/**
 * Turn a form schema + the visitor's answers + page/tool context into the
 * POST /api/v2/leads body.
 *
 * - `fields`  CRM-keyed values. A schema field with `crm: "Current_Supplier"`
 *             lands in that Zoho field; `crm` may also be a function returning
 *             several CRM keys (e.g. a full-name field → First_Name/Last_Name).
 *             Option fields send each option's `crm` value (or its label).
 *             Empty and zero values are never sent.
 * - `answers` What the visitor saw and chose, label → display value, for the
 *             Lead's Note.
 * - `context` Free-form tool/page state, also for the Note.
 *
 * The backend maps `fields` against live Zoho metadata; anything it can't
 * place is kept in the Note, so schemas can't lose data by getting a key wrong.
 */
import { attributionToCrm, getAttribution, sanitiseAttribution } from "./attribution";
import { isEmpty, isVisible } from "./validation";

export const DEFAULT_LEAD_SOURCE = "Website Contact Form";

/** Every field in a schema, in order, whether it uses steps, rows or a flat list. */
export function flattenFields(schema) {
  const groups = schema.steps ? schema.steps.map((s) => s.fields) : [schema.fields || []];
  return groups.flatMap((group) => group.flatMap((entry) => (Array.isArray(entry) ? entry : [entry])));
}

function optionFor(field, value) {
  return (field.options || []).find((o) => o.value === value);
}

/** Human-readable value for the Note. */
export function displayValue(field, value) {
  if (field.type === "file") {
    return (value || []).map((f) => f.name).join(", ");
  }
  if (Array.isArray(value)) {
    return value.map((v) => optionFor(field, v)?.label || v).join(", ");
  }
  if (field.options) return optionFor(field, value)?.label || value;
  if (field.type === "checkbox") return value ? "Yes" : "No";
  return value;
}

/** Value to send to the CRM for option-based fields. */
function crmValue(field, value) {
  if (!field.options) return value;
  const toCrm = (v) => {
    const option = optionFor(field, v);
    return option ? (option.crm !== undefined ? option.crm : option.label) : v;
  };
  return Array.isArray(value) ? value.map(toCrm).flat().filter((v) => !isEmpty(v)) : toCrm(value);
}

function isSendable(value) {
  if (isEmpty(value)) return false;
  if (typeof value === "number" && value === 0) return false;
  return true;
}

function mergeCrm(target, source) {
  Object.entries(source || {}).forEach(([key, value]) => {
    if (!isSendable(value)) return;
    if (Array.isArray(value) && Array.isArray(target[key])) {
      target[key] = Array.from(new Set([...target[key], ...value]));
    } else {
      target[key] = value;
    }
  });
  return target;
}

export function buildCrmFields(schema, values, context = {}) {
  const fields = {};
  flattenFields(schema).forEach((field) => {
    if (!field.crm || !isVisible(field, values, context)) return;
    const value = values[field.name];
    if (!isSendable(value)) return;
    if (typeof field.crm === "function") {
      mergeCrm(fields, field.crm(value, values, context));
    } else {
      mergeCrm(fields, { [field.crm]: crmValue(field, value) });
    }
  });
  // Form-level derived values (calculator results → Current_rates text, etc.).
  if (typeof schema.contextToCrm === "function") {
    mergeCrm(fields, schema.contextToCrm(context, values));
  }
  if (typeof schema.summary === "function") {
    mergeCrm(fields, { Interested_In: schema.summary(values, context) });
  }
  return fields;
}

export function buildAnswers(schema, values, context = {}) {
  return flattenFields(schema)
    .filter((field) => field.type !== "hidden" && field.type !== "file" && isVisible(field, values, context))
    .filter((field) => !isEmpty(values[field.name]))
    .map((field) => ({
      label: field.answerLabel || field.label,
      value: displayValue(field, values[field.name]),
    }));
}

export function buildSubmission({
  schema, values, context = {}, submissionId, marketingConsent, elapsedMs, hp, fileKeys = [],
  attribution = getAttribution(),
}) {
  const crm = schema.crm || {};
  // The caller can supply attribution in tests and integrations. Re-sanitise
  // here so bypassing the browser storage cannot carry free-form values into
  // CRM fields or the backend Note payload.
  const safeAttribution = sanitiseAttribution(attribution);
  // layout / leadSource / tags may be static or derived from the answers.
  const resolve = (v) => (typeof v === "function" ? v(values, context) : v);
  const fields = {
    ...attributionToCrm(safeAttribution),
    ...buildCrmFields(schema, values, context),
  };
  Object.keys(fields).forEach((key) => {
    if (!isSendable(fields[key])) delete fields[key];
  });
  return {
    id: submissionId,
    form: {
      id: schema.id,
      label: schema.label,
      layout: resolve(crm.layout) || "Standard",
      lead_source: resolve(crm.leadSource) || DEFAULT_LEAD_SOURCE,
      tags: resolve(crm.tags) || [],
    },
    fields,
    answers: buildAnswers(schema, values, context),
    context: { page_path: typeof window !== "undefined" ? window.location.pathname : "", ...context },
    attribution: safeAttribution,
    consent: {
      marketing: schema.consent?.marketing === false ? false : Boolean(marketingConsent),
      timestamp: new Date().toISOString(),
    },
    files: fileKeys,
    hp: hp || "",
    elapsed_ms: elapsedMs,
  };
}

/** Split "Jane van der Berg" → First_Name "Jane", Last_Name "van der Berg". */
export function splitName(value) {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  return { First_Name: parts[0], Last_Name: parts.slice(1).join(" ") };
}
